/* eslint-disable */

type Lab = { l: number; a: number; b: number };
type Rgb = { r: number; g: number; b: number };
type Hsv = { h: number; s: number; v: number };
type Hsl = { h: number; s: number; l: number };
type Lc = { l: number; c: number };

// Alternative representation of (L_cusp, C_cusp)
// Encoded so S = C_cusp/L_cusp and T = C_cusp/(1-L_cusp)
// The maximum value for C in the triangle is then found as fmin(S*L, T*(1-L)), for a given L
type St = { s: number; t: number };

const PI = 3.1415926535897932384626433832795028841971693993751058209749445923078164062;

function clamp(x: number, min: number, max: number): number {
  if (x < min) {
    return min;
  }
  if (x > max) {
    return max;
  }

  return x;
}

function sgn(x: number): number {
  return (0 < x ? 1 : 0) - (x < 0 ? 1 : 0);
}

function srgbTransferFunction(a: number): number {
  return 0.0031308 >= a ? 12.92 * a : 1.055 * Math.pow(a, 1 / 2.4) - 0.055;
}

function srgbTransferFunctionInv(a: number): number {
  return 0.04045 < a ? Math.pow((a + 0.055) / 1.055, 2.4) : a / 12.92;
}

function linearSrgbToOklab(c: Rgb): Lab {
  const { r, g, b } = c;
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  return {
    l: 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
    a: 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
  };
}

function oklabToLinearSrgb(c: Lab): Rgb {
  const { l: lIn, a: aIn, b: bIn } = c;
  const l_ = lIn + 0.3963377774 * aIn + 0.2158037573 * bIn;
  const m_ = lIn - 0.1055613458 * aIn - 0.0638541728 * bIn;
  const s_ = lIn - 0.0894841775 * aIn - 1.291485548 * bIn;

  const l = l_ ** 3;
  const m = m_ ** 3;
  const s = s_ ** 3;

  return {
    r: +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    b: -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  };
}

// Finds the maximum saturation possible for a given hue that fits in sRGB
// Saturation here is defined as S = C/L
// a and b must be normalized so a^2 + b^2 == 1
function computeMaxSaturation(a: number, b: number): number {
  // Max saturation will be when one of r, g or b goes below zero.

  // Select different coefficients depending on which component goes below zero first
  let k0, k1, k2, k3, k4, wl, wm, ws;

  if (-1.88170328 * a - 0.80936493 * b > 1) {
    // Red component
    k0 = +1.19086277;
    k1 = +1.76576728;
    k2 = +0.59662641;
    k3 = +0.75515197;
    k4 = +0.56771245;
    wl = +4.0767416621;
    wm = -3.3077115913;
    ws = +0.2309699292;
  } else if (1.81444104 * a - 1.19445276 * b > 1) {
    // Green component
    k0 = +0.73956515;
    k1 = -0.45954404;
    k2 = +0.08285427;
    k3 = +0.1254107;
    k4 = +0.14503204;
    wl = -1.2684380046;
    wm = +2.6097574011;
    ws = -0.3413193965;
  } else {
    // Blue component
    k0 = +1.35733652;
    k1 = -0.00915799;
    k2 = -1.1513021;
    k3 = -0.50559606;
    k4 = +0.00692167;
    wl = -0.0041960863;
    wm = -0.7034186147;
    ws = +1.707614701;
  }

  // Approximate max saturation using a polynomial:
  let S = k0 + k1 * a + k2 * b + k3 * a ** 2 + k4 * a * b;

  // Do one step Halley's method to get closer
  // this gives an error less than 10e6, except for some blue hues where the dS/dh is close to infinite
  // this should be sufficient for most applications, otherwise do two/three steps

  const k_l = +0.3963377774 * a + 0.2158037573 * b;
  const k_m = -0.1055613458 * a - 0.0638541728 * b;
  const k_s = -0.0894841775 * a - 1.291485548 * b;

  const l_ = 1 + S * k_l;
  const m_ = 1 + S * k_m;
  const s_ = 1 + S * k_s;

  const l = l_ ** 3;
  const m = m_ ** 3;
  const s = s_ ** 3;

  const l_dS = 3 * k_l * l_ ** 2;
  const m_dS = 3 * k_m * m_ ** 2;
  const s_dS = 3 * k_s * s_ ** 2;

  const l_dS2 = 6 * k_l ** 2 * l_;
  const m_dS2 = 6 * k_m ** 2 * m_;
  const s_dS2 = 6 * k_s ** 2 * s_;

  const f = wl * l + wm * m + ws * s;
  const f1 = wl * l_dS + wm * m_dS + ws * s_dS;
  const f2 = wl * l_dS2 + wm * m_dS2 + ws * s_dS2;

  S = S - (f * f1) / (f1 ** 2 - 0.5 * f * f2);

  return S;
}

interface LC {
  L: number;
  C: number;
}

interface RGB {
  r: number;
  g: number;
  b: number;
}

// finds L_cusp and C_cusp for a given hue
// a and b must be normalized so a^2 + b^2 == 1
function findCusp(a: number, b: number): LC {
  // First, find the maximum saturation (saturation S = C/L)
  const S_cusp = computeMaxSaturation(a, b);

  // Convert to linear sRGB to find the first point where at least one of r,g or b >= 1:
  const rgb_at_max = oklabToLinearSrgb({ l: 1, a: S_cusp * a, b: S_cusp * b });
  const L_cusp = Math.cbrt(
    1.0 / Math.max(Math.max(rgb_at_max.r, rgb_at_max.g), rgb_at_max.b),
  );
  const C_cusp = L_cusp * S_cusp;

  return { L: L_cusp, C: C_cusp };
}

// Finds intersection of the line defined by
// L = L0 * (1 - t) + t * L1;
// C = t * C1;
// a and b must be normalized so a^2 + b^2 == 1
function findGamutIntersection(
  a: number,
  b: number,
  L1: number,
  C1: number,
  L0: number,
  cusp: LC,
): number {
  // Find the intersection for upper and lower half separately
  let t;
  if ((L1 - L0) * cusp.C - (cusp.L - L0) * C1 <= 0.0) {
    // Lower half
    t = (cusp.C * L0) / (C1 * cusp.L + cusp.C * (L0 - L1));
  } else {
    // Upper half

    // First intersect with triangle
    t = (cusp.C * (L0 - 1.0)) / (C1 * (cusp.L - 1.0) + cusp.C * (L0 - L1));

    // Then one step Halley's method
    {
      let dL = L1 - L0;
      let dC = C1;

      let k_l = +0.3963377774 * a + 0.2158037573 * b;
      let k_m = -0.1055613458 * a - 0.0638541728 * b;
      let k_s = -0.0894841775 * a - 1.291485548 * b;

      let l_dt = dL + dC * k_l;
      let m_dt = dL + dC * k_m;
      let s_dt = dL + dC * k_s;

      // If higher accuracy is required, 2 or 3 iterations of the following block can be used:
      {
        let L = L0 * (1.0 - t) + t * L1;
        let C = t * C1;

        let l_ = L + C * k_l;
        let m_ = L + C * k_m;
        let s_ = L + C * k_s;

        let l = l_ ** 3;
        let m = m_ ** 3;
        let s = s_ ** 3;

        let ldt = 3 * l_dt * l_ ** 2;
        let mdt = 3 * m_dt * m_ ** 2;
        let sdt = 3 * s_dt * s_ ** 2;

        let ldt2 = 6 * l_dt ** 2 * l_;
        let mdt2 = 6 * m_dt ** 2 * m_;
        let sdt2 = 6 * s_dt ** 2 * s_;

        let r = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s - 1;
        let r1 = 4.0767416621 * ldt - 3.3077115913 * mdt + 0.2309699292 * sdt;
        let r2 =
          4.0767416621 * ldt2 - 3.3077115913 * mdt2 + 0.2309699292 * sdt2;

        let u_r = r1 / (r1 ** 2 - 0.5 * r * r2);
        let t_r = -r * u_r;

        let g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s - 1;
        let g1 = -1.2684380046 * ldt + 2.6097574011 * mdt - 0.3413193965 * sdt;
        let g2 =
          -1.2684380046 * ldt2 + 2.6097574011 * mdt2 - 0.3413193965 * sdt2;

        let u_g = g1 / (g1 ** 2 - 0.5 * g * g2);
        let t_g = -g * u_g;

        let b = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s - 1;
        let b1 = -0.0041960863 * ldt - 0.7034186147 * mdt + 1.707614701 * sdt;
        let b2 =
          -0.0041960863 * ldt2 - 0.7034186147 * mdt2 + 1.707614701 * sdt2;

        let u_b = b1 / (b1 ** 2 - 0.5 * b * b2);
        let t_b = -b * u_b;

        t_r = u_r >= 0.0 ? t_r : Number.MAX_VALUE;
        t_g = u_g >= 0.0 ? t_g : Number.MAX_VALUE;
        t_b = u_b >= 0.0 ? t_b : Number.MAX_VALUE;

        t += Math.min(t_r, Math.min(t_g, t_b));
      }
    }
  }

  return t;
}

function findGamutIntersectionB(
  a: number,
  b: number,
  L1: number,
  C1: number,
  L0: number,
): number {
  // Find the cusp of the gamut triangle
  const cusp = findCusp(a, b);

  return findGamutIntersection(a, b, L1, C1, L0, cusp);
}

function gamutClipPreserveChroma(rgb: RGB): RGB {
  if (
    rgb.r < 1 &&
    rgb.g < 1 &&
    rgb.b < 1 &&
    rgb.r > 0 &&
    rgb.g > 0 &&
    rgb.b > 0
  )
    return rgb;

  const lab = linearSrgbToOklab(rgb);

  const L = lab.l;
  const eps = 0.00001;
  const C = Math.max(eps, Math.sqrt(lab.a * lab.a + lab.b * lab.b));
  const a_ = lab.a / C;
  const b_ = lab.b / C;

  const L0 = clamp(L, 0, 1);

  const t = findGamutIntersectionB(a_, b_, L, C, L0);
  const L_clipped = L0 * (1 - t) + t * L;
  const C_clipped = t * C;

  return oklabToLinearSrgb({
    l: L_clipped,
    a: C_clipped * a_,
    b: C_clipped * b_,
  });
}

function gamutClipProjectTo0_5(rgb: RGB): RGB {
  if (
    rgb.r < 1 &&
    rgb.g < 1 &&
    rgb.b < 1 &&
    rgb.r > 0 &&
    rgb.g > 0 &&
    rgb.b > 0
  )
    return rgb;

  const lab = linearSrgbToOklab(rgb);

  const L = lab.l;
  const eps = 0.00001;
  const C = Math.max(eps, Math.sqrt(lab.a * lab.a + lab.b * lab.b));
  const a_ = lab.a / C;
  const b_ = lab.b / C;

  const L0 = 0.5;

  const t = findGamutIntersectionB(a_, b_, L, C, L0);
  const L_clipped = L0 * (1 - t) + t * L;
  const C_clipped = t * C;

  return oklabToLinearSrgb({
    l: L_clipped,
    a: C_clipped * a_,
    b: C_clipped * b_,
  });
}

function gamutClipProjectToL_cusp(rgb: RGB): RGB {
  if (
    rgb.r < 1 &&
    rgb.g < 1 &&
    rgb.b < 1 &&
    rgb.r > 0 &&
    rgb.g > 0 &&
    rgb.b > 0
  )
    return rgb;

  const lab = linearSrgbToOklab(rgb);

  const L = lab.l;
  const eps = 0.00001;
  const C = Math.max(eps, Math.sqrt(lab.a * lab.a + lab.b * lab.b));
  const a_ = lab.a / C;
  const b_ = lab.b / C;

  // The cusp is computed here and in findGamutIntersection, an optimized solution would only compute it once.
  const cusp = findCusp(a_, b_);

  const L0 = cusp.L;

  const t = findGamutIntersectionB(a_, b_, L, C, L0);

  const L_clipped = L0 * (1 - t) + t * L;
  const C_clipped = t * C;

  return oklabToLinearSrgb({
    l: L_clipped,
    a: C_clipped * a_,
    b: C_clipped * b_,
  });
}

function gamutClipAdaptiveL0_0_5(rgb: RGB, alpha = 0.05): RGB {
  if (
    rgb.r < 1 &&
    rgb.g < 1 &&
    rgb.b < 1 &&
    rgb.r > 0 &&
    rgb.g > 0 &&
    rgb.b > 0
  )
    return rgb;

  const lab = linearSrgbToOklab(rgb);

  const L = lab.l;
  const eps = 0.00001;
  const C = Math.max(eps, Math.sqrt(lab.a * lab.a + lab.b * lab.b));
  const a_ = lab.a / C;
  const b_ = lab.b / C;

  const Ld = L - 0.5;
  const e1 = 0.5 + Math.abs(Ld) + alpha * C;
  const L0 =
    0.5 *
    (1.0 + Math.sign(Ld) * (e1 - Math.sqrt(e1 * e1 - 2.0 * Math.abs(Ld))));

  const t = findGamutIntersectionB(a_, b_, L, C, L0);
  const L_clipped = L0 * (1.0 - t) + t * L;
  const C_clipped = t * C;

  return oklabToLinearSrgb({
    l: L_clipped,
    a: C_clipped * a_,
    b: C_clipped * b_,
  });
}

function gamutClipAdaptiveL0_L_cusp(rgb: RGB, alpha = 0.05): RGB {
  if (
    rgb.r < 1 &&
    rgb.g < 1 &&
    rgb.b < 1 &&
    rgb.r > 0 &&
    rgb.g > 0 &&
    rgb.b > 0
  )
    return rgb;

  const lab = linearSrgbToOklab(rgb);

  const L = lab.l;
  const eps = 0.00001;
  const C = Math.max(eps, Math.sqrt(lab.a * lab.a + lab.b * lab.b));
  const a_ = lab.a / C;
  const b_ = lab.b / C;

  // The cusp is computed here and in findGamutIntersection, an optimized solution would only compute it once.
  const cusp = findCusp(a_, b_);

  const Ld = L - cusp.L;
  const k = 2.0 * (Ld > 0 ? 1.0 - cusp.L : cusp.L);

  const e1 = 0.5 * k + Math.abs(Ld) + (alpha * C) / k;
  const L0 =
    cusp.L +
    0.5 * (Math.sign(Ld) * (e1 - Math.sqrt(e1 * e1 - 2.0 * k * Math.abs(Ld))));

  const t = findGamutIntersectionB(a_, b_, L, C, L0);
  const L_clipped = L0 * (1.0 - t) + t * L;
  const C_clipped = t * C;

  return oklabToLinearSrgb({
    l: L_clipped,
    a: C_clipped * a_,
    b: C_clipped * b_,
  });
}

function toe(x: number): number {
  const k_1 = 0.206;
  const k_2 = 0.03;
  const k_3 = (1.0 + k_1) / (1.0 + k_2);
  return (
    0.5 *
    (k_3 * x -
      k_1 +
      Math.sqrt((k_3 * x - k_1) * (k_3 * x - k_1) + 4 * k_2 * k_3 * x))
  );
}

function toeInv(x: number): number {
  const k_1 = 0.206;
  const k_2 = 0.03;
  const k_3 = (1.0 + k_1) / (1.0 + k_2);
  return (x * x + k_1 * x) / (k_3 * (x + k_2));
}

interface ST {
  S: number;
  T: number;
}

function toST(cusp: LC): ST {
  const L = cusp.L;
  const C = cusp.C;
  return { S: C / L, T: C / (1 - L) };
}

function getSTMid(a_: number, b_: number): ST {
  const S =
    0.11516993 +
    1.0 /
      (+7.4477897 +
        4.1590124 * b_ +
        a_ *
          (-2.19557347 +
            1.75198401 * b_ +
            a_ *
              (-2.13704948 -
                10.02301043 * b_ +
                a_ * (-4.24894561 + 5.38770819 * b_ + 4.69891013 * a_))));

  const T =
    0.11239642 +
    1.0 /
      (+1.6132032 -
        0.68124379 * b_ +
        a_ *
          (+0.40370612 +
            0.90148123 * b_ +
            a_ *
              (-0.27087943 +
                0.6122399 * b_ +
                a_ * (+0.00299215 - 0.45399568 * b_ - 0.14661872 * a_))));

  return { S, T };
}

interface Cs {
  C_0: number;
  C_mid: number;
  C_max: number;
}

function getCs(L: number, a_: number, b_: number): Cs {
  const cusp = findCusp(a_, b_);

  const C_max = findGamutIntersection(a_, b_, L, 1, L, cusp);
  const ST_max = toST(cusp);

  // Scale factor to compensate for the curved part of gamut shape:
  const k = C_max / Math.min(L * ST_max.S, (1 - L) * ST_max.T);

  let C_mid;
  {
    const ST_mid = getSTMid(a_, b_);

    // Use a soft minimum function, instead of a sharp triangle shape to get a smooth value for chroma.
    const C_a = L * ST_mid.S;
    const C_b = (1.0 - L) * ST_mid.T;
    C_mid =
      0.9 *
      k *
      Math.sqrt(
        Math.sqrt(
          1.0 / (1.0 / (C_a * C_a * C_a * C_a) + 1.0 / (C_b * C_b * C_b * C_b)),
        ),
      );
  }

  let C_0;
  {
    // for C_0, the shape is independent of hue, so ST are constant. Values picked to roughly be the average values of ST.
    const C_a = L * 0.4;
    const C_b = (1.0 - L) * 0.8;

    // Use a soft minimum function, instead of a sharp triangle shape to get a smooth value for chroma.
    C_0 = Math.sqrt(1.0 / (1.0 / (C_a * C_a) + 1.0 / (C_b * C_b)));
  }

  return { C_0, C_mid, C_max };
}

interface HSL {
  h: number;
  s: number;
  l: number;
}

export function okhslToSrgb(hsl: HSL): RGB {
  const h = hsl.h;
  const s = hsl.s;
  const l = hsl.l;

  if (l === 1.0) {
    return { r: 1.0, g: 1.0, b: 1.0 };
  } else if (l === 0.0) {
    return { r: 0.0, g: 0.0, b: 0.0 };
  }

  const a_ = Math.cos(2.0 * Math.PI * h);
  const b_ = Math.sin(2.0 * Math.PI * h);
  const L = toeInv(l);

  const cs = getCs(L, a_, b_);
  const C_0 = cs.C_0;
  const C_mid = cs.C_mid;
  const C_max = cs.C_max;

  const mid = 0.8;
  const mid_inv = 1.25;

  let C, t, k_0, k_1, k_2;

  if (s < mid) {
    t = mid_inv * s;

    k_1 = mid * C_0;
    k_2 = 1.0 - k_1 / C_mid;

    C = (t * k_1) / (1.0 - k_2 * t);
  } else {
    t = (s - mid) / (1 - mid);

    k_0 = C_mid;
    k_1 = ((1.0 - mid) * C_mid * C_mid * mid_inv * mid_inv) / C_0;
    k_2 = 1.0 - k_1 / (C_max - C_mid);

    C = k_0 + (t * k_1) / (1.0 - k_2 * t);
  }

  const rgb = oklabToLinearSrgb({ l, a: C * a_, b: C * b_ });
  const transferedRGB = {
    r: srgbTransferFunction(rgb.r),
    g: srgbTransferFunction(rgb.g),
    b: srgbTransferFunction(rgb.b),
  };
  return {
    r: transferedRGB.r < 0 ? 0 : transferedRGB.r,
    g: transferedRGB.g < 0 ? 0 : transferedRGB.g,
    b: transferedRGB.b < 0 ? 0 : transferedRGB.b,
  };
}

interface HSV {
  h: number;
  s: number;
  v: number;
}

export function okhsvToSrgb(hsv: HSV): RGB {
  let h = hsv.h;
  let s = hsv.s;
  let v = hsv.v;

  let a_ = Math.cos(2 * Math.PI * h);
  let b_ = Math.sin(2 * Math.PI * h);

  let cusp = findCusp(a_, b_);
  let ST_max = toST(cusp);
  let S_max = ST_max.S;
  let T_max = ST_max.T;
  let S_0 = 0.5;
  let k = 1 - S_0 / S_max;

  // first we compute L and V as if the gamut is a perfect triangle:

  // L, C when v==1:
  let L_v = 1 - (s * S_0) / (S_0 + T_max - T_max * k * s);
  let C_v = (s * T_max * S_0) / (S_0 + T_max - T_max * k * s);

  let L = v * L_v;
  let C = v * C_v;

  // then we compensate for both toe and the curved top part of the triangle:
  let L_vt = toeInv(L_v);
  let C_vt = (C_v * L_vt) / L_v;

  let L_new = toeInv(L);
  C = (C * L_new) / L;
  L = L_new;

  let rgb_scale = oklabToLinearSrgb({ l: L_vt, a: a_ * C_vt, b: b_ * C_vt });
  let scale_L = Math.cbrt(
    1 / Math.max(Math.max(rgb_scale.r, rgb_scale.g), Math.max(rgb_scale.b, 0)),
  );

  L = L * scale_L;
  C = C * scale_L;

  let rgb = oklabToLinearSrgb({ l: L, a: C * a_, b: C * b_ });
  const transferedRGB = {
    r: srgbTransferFunction(rgb.r),
    g: srgbTransferFunction(rgb.g),
    b: srgbTransferFunction(rgb.b),
  };
  return {
    r: transferedRGB.r < 0 ? 0 : transferedRGB.r,
    g: transferedRGB.g < 0 ? 0 : transferedRGB.g,
    b: transferedRGB.b < 0 ? 0 : transferedRGB.b,
  };
}
